export type PaymentSystem = 'asaas' | 'stripe' | 'pagarme' | 'mercadopago' | 'paypal' | 'pix';
export const GATEWAY_PROVIDERS = ['asaas', 'stripe', 'paypal'] as const;
export type GatewayProvider = typeof GATEWAY_PROVIDERS[number];
export type PaymentCurrency = 'BRL' | 'USD';
export type PaymentHistoryStatus = 'PAID' | 'PENDING' | 'CANCELED' | 'REMOVED' | 'EXPIRED' | 'FAILED' | 'REFUNDED' | 'UNKNOWN';

export const PAYMENT_SYSTEM_LABELS: Record<PaymentSystem, string> = {
  asaas: 'Asaas',
  stripe: 'Stripe',
  pagarme: 'Pagar.me',
  mercadopago: 'Mercado Pago',
  paypal: 'PayPal',
  pix: 'Pix direto na conta',
};

export function isGatewayProvider(
  value: string | null | undefined,
): value is GatewayProvider {
  return GATEWAY_PROVIDERS.some((provider) => provider === value);
}

/** payment_accounts table — represents a payer (may differ from student; mirrors Asaas customer) */
export interface PaymentAccount {
  id: number;
  payment_system: PaymentSystem;
  external_customer_id: string;
  name: string | null;
  email: string | null;
  cpf_cnpj: string | null;
  phone: string | null;
  mobile_phone: string | null;
  postal_code: string | null;
  address: string | null;
  address_number: string | null;
  address_complement: string | null;
  neighborhood: string | null;
  created_at: string;
  updated_at: string;
}

/** students_payment_accounts junction table */
export interface StudentPaymentAccount {
  id: number;
  student_id: number;
  payment_account_id: number;
  created_at: string;
  /** Joined payment account (when using select with join) */
  payment_accounts?: PaymentAccount;
}

export interface PaymentHistoryError {
  id: number;
  payment_history_id: number;
  provider: string;
  gateway_event: string | null;
  error_kind: string;
  error_code: string | null;
  error_message: string;
  raw_status: string | null;
  normalized_status: PaymentHistoryStatus | null;
  error_payload?: Record<string, unknown> | null;
  created_at: string;
  /** Enrichment from payment_history (list-payment-history-errors). */
  external_payment_id?: string | null;
  payment_status?: string | null;
  payment_value?: number | null;
  payment_currency?: PaymentCurrency | null;
  payment_account_id?: number | null;
}

export interface PaymentHistoryErrorFilters {
  search?: string;
  paymentId?: string;
  provider?: PaymentSystem | '';
  dateFrom?: string;
  dateTo?: string;
}

export const PAYMENT_ERROR_PROVIDER_OPTIONS: Array<{ value: PaymentSystem; label: string }> = [
  { value: 'asaas', label: PAYMENT_SYSTEM_LABELS.asaas },
  { value: 'stripe', label: PAYMENT_SYSTEM_LABELS.stripe },
  { value: 'paypal', label: PAYMENT_SYSTEM_LABELS.paypal },
  { value: 'pagarme', label: PAYMENT_SYSTEM_LABELS.pagarme },
  { value: 'mercadopago', label: PAYMENT_SYSTEM_LABELS.mercadopago },
];

export interface PaymentHistory {
  id: number;
  payment_account_id: number;
  student_subscription_id: number | null;
  external_payment_id: string;
  external_subscription_id: string | null;
  /** Decimal amount in the persisted currency, not cents. */
  value: number;
  currency: PaymentCurrency;
  status: string;
  external_created_date: string;
  due_date: string | null;
  paid_at: string | null;
  payment_link_url: string | null;
  created_at: string;
  /** RECURRING = assinatura recorrente; SINGLE = pagamento avulso/à vista */
  charge_type: 'RECURRING' | 'SINGLE' | null;
  latest_error?: PaymentHistoryError | null;
}

export type PaymentWebhookFailureStatus = 'pending' | 'processing' | 'recovered';
export type PaymentWebhookFailureListStatus = 'open' | PaymentWebhookFailureStatus;

export interface PaymentWebhookFailure {
  id: number;
  provider: GatewayProvider;
  gateway_event: string | null;
  provider_event_id: string | null;
  payload_json: Record<string, unknown> | null;
  raw_body: string | null;
  external_payment_id: string | null;
  external_subscription_id: string | null;
  external_customer_id: string | null;
  payment_history_id: number | null;
  student_subscription_id: number | null;
  student_id: number | null;
  student_name?: string | null;
  failure_code: string;
  failure_reason: string;
  http_status_returned: number | null;
  status: PaymentWebhookFailureStatus;
  attempt_count: number;
  last_attempt_at: string | null;
  last_error_message: string | null;
  received_at: string;
  recovered_at: string | null;
  processing_started_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentWebhookFailureFilters {
  search?: string;
  paymentId?: string;
  provider?: GatewayProvider | '';
  status?: PaymentWebhookFailureListStatus;
  dateFrom?: string;
  dateTo?: string;
}
